<script lang="ts">
	import { Alert, Button, Container, Loader } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { format } from 'svelte-i18n';
	import { timeFormatUS } from 'src/helpers/date_helper';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import {
		normalizeTariffTables,
		buildPayload,
		type TariffTables,
	} from './DaisyEM/helpers/daisy_em_tariff_helpers';
	import DayPatternsSection from './DaisyEM/components/DayPatternsSection.svelte';
	import WeekPatternsSection from './DaisyEM/components/WeekPatternsSection.svelte';
	import HolidaysSection from './DaisyEM/components/HolidaysSection.svelte';
	import SeasonScheduleSection from './DaisyEM/components/SeasonScheduleSection.svelte';

	const formatD = timeFormatUS() ? 'm.d.Y' : 'd.m.Y';

	export let device;

	$: weekdayEntries = (() => {
		const days = [
			{ label: $format('option_sunday', { default: 'Sun' }), dataIndex: 0 },
			{ label: $format('option_monday', { default: 'Mon' }), dataIndex: 1 },
			{ label: $format('option_tuesday', { default: 'Tue' }), dataIndex: 2 },
			{ label: $format('option_wednesday', { default: 'Wed' }), dataIndex: 3 },
			{ label: $format('option_thursday', { default: 'Thu' }), dataIndex: 4 },
			{ label: $format('option_friday', { default: 'Fri' }), dataIndex: 5 },
			{ label: $format('option_saturday', { default: 'Sat' }), dataIndex: 6 },
		];
		return timeFormatUS() ? days : [...days.slice(1), days[0]];
	})();

	let loading = false;
	let tariffTables: TariffTables | null = null;
	let fetchError = false;

	/** Fetches tariff tables from the device and populates the local state. */
	const loadTariffTables = async () => {
		fetchError = false;
		const res = await gen2Request(device, 'DaisyEM.GetTariffTables', { id: device.channel });
		const data = (res as any)?.data || res;
		if (res.isok && (data as any)?.tariff_tables) {
			tariffTables = normalizeTariffTables((data as any).tariff_tables);
		} else {
			fetchError = true;
		}
		return tariffTables;
	};

	/** Builds the payload and persists the tariff tables to the device via RPC. */
	const saveTariffTables = async () => {
		if (!tariffTables) return;
		loading = true;
		const payload = buildPayload(tariffTables);
		const res = await gen2Request(device, 'DaisyEM.SetTariffTables', {
			id: device.channel,
			tariff_tables: payload,
		});
		await onSettingsResponse(res, device, {
			success: $format('daisy_em_tariff_save_success', {
				default: 'Tariff tables saved successfully.',
			}),
			danger: $format('error_global'),
		});
		loading = false;
	};
</script>

{#await loadTariffTables()}
	<Loader />
{:then _}
	{#if fetchError || !tariffTables}
		<Alert
			type="danger"
			heading={$format('daisy_em_tariff_load_error', {
				default: 'Failed to load tariff tables from the device.',
			})}
		/>
	{:else}
		<DayPatternsSection bind:tariffTables />
		<WeekPatternsSection bind:tariffTables {weekdayEntries} />
		<HolidaysSection bind:tariffTables {formatD} />
		<SeasonScheduleSection bind:tariffTables />

		<Button {loading} width="full" size="m" on:click={saveTariffTables}>
			{$format('button_save')}
		</Button>
	{/if}
{/await}
